/**
 * Minimal test page per print role (silent print verification).
 */

import {
  PRINT_ROLE,
  normalizePrintRole,
  roleToPrintKind,
  PRINT_ROLE_META
} from './printerRoles';

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} role
 * @param {{ displayName?: string, deviceName?: string, language?: string }} [opts]
 */
export function buildPrinterRoleTestHtml(role, opts = {}) {
  const id = normalizePrintRole(role);
  const kind = roleToPrintKind(id);
  const paperHint = PRINT_ROLE_META[id]?.paperHint || '';
  const lang = opts.language === 'ar' ? 'ar' : 'fr';
  const isRTL = lang === 'ar';
  const title =
    lang === 'ar' ? 'اختبار طابعة' : 'Test imprimante';
  const roleLabel =
    lang === 'ar'
      ? ({
          [PRINT_ROLE.RECEIPT]: 'إيصال / حراري',
          [PRINT_ROLE.DOCUMENT]: 'مستند A4/A5',
          [PRINT_ROLE.LABEL]: 'ملصق'
        }[id] || id)
      : ({
          [PRINT_ROLE.RECEIPT]: 'Reçu / thermique',
          [PRINT_ROLE.DOCUMENT]: 'Document A4/A5',
          [PRINT_ROLE.LABEL]: 'Étiquette'
        }[id] || id);
  const device =
    opts.displayName || opts.deviceName || (lang === 'ar' ? 'غير معيّن' : 'non assigné');
  const when = new Date().toLocaleString(lang === 'ar' ? 'ar-MR' : 'fr-FR');

  const widthMm =
    kind === 'thermal' ? 72 : kind === 'label-sheet' ? 50 : 190;
  const pageCss =
    kind === 'thermal'
      ? `@page { size: 80mm auto; margin: 2mm; }
         body { width: 72mm; font-size: 11px; }`
      : kind === 'label-sheet'
        ? `@page { size: 50mm 30mm; margin: 1mm; }
           body { width: 48mm; font-size: 10px; }`
        : `@page { size: A5 portrait; margin: 12mm; }
           body { font-size: 13px; }`;

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} — ${escapeHtml(roleLabel)}</title>
  <style>
    ${pageCss}
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 4px;
      font-family: system-ui, "Segoe UI", Tahoma, sans-serif;
      color: #111;
      max-width: ${widthMm}mm;
    }
    h1 { font-size: 1.1em; margin: 0 0 6px; }
    .meta { margin: 4px 0; line-height: 1.35; }
    .line { border-top: 1px dashed #333; margin: 8px 0; }
    .ok { font-weight: 600; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta"><strong>Rôle</strong>: ${escapeHtml(roleLabel)}</div>
  <div class="meta"><strong>Format</strong>: ${escapeHtml(paperHint || kind)}</div>
  <div class="meta"><strong>Imprimante</strong>: ${escapeHtml(device)}</div>
  <div class="meta"><strong>Heure</strong>: ${escapeHtml(when)}</div>
  <div class="line"></div>
  <p class="ok">${
    lang === 'ar'
      ? 'إذا ظهر هذا النص، فالتوجيه يعمل.'
      : 'Si ce texte imprime, le routage rôle → imprimante fonctionne.'
  }</p>
</body>
</html>`;
}
